import { Loader2, RotateCcw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';

interface PromptReviewStepProps {
  promptText: string;
  onPromptChange: (text: string) => void;
  originalPromptText: string;
  onReset: () => void;
  onBack: () => void;
  onGenerate: () => void;
  isSubmitting?: boolean;
  historicalActivityCount: number;
}

export default function PromptReviewStep({
  promptText,
  onPromptChange,
  originalPromptText,
  onReset,
  onBack,
  onGenerate,
  isSubmitting,
  historicalActivityCount,
}: PromptReviewStepProps) {
  const isModified = promptText !== originalPromptText;

  return (
    <div className="space-y-6">
      <Card className="bg-admin-dark-800 border-admin-slate-300/10">
        <CardHeader>
          <CardTitle className="text-heading-4 text-admin-slate-400">
            Review Prompt
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info banner */}
          <div className="flex items-start gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
            <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-600">
              Changes here are temporary and won&apos;t be saved to the database.
              The original prompt template remains unchanged.
            </p>
          </div>

          {/* Editable prompt textarea */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="promptText" className="text-admin-slate-400">
                Interpolated Prompt
              </Label>
              {isModified && (
                <button
                  type="button"
                  onClick={onReset}
                  className="flex items-center gap-1 text-xs text-admin-slate-500 hover:text-admin-slate-400 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset to original
                </button>
              )}
            </div>
            <textarea
              id="promptText"
              rows={16}
              value={promptText}
              onChange={(e) => onPromptChange(e.target.value)}
              className="w-full px-4 py-3 bg-admin-dark-700 border border-admin-slate-300/10 rounded-md text-admin-slate-400 placeholder-admin-slate-600 focus:outline-none focus:ring-2 focus:ring-admin-accent-blue transition-colors resize-none font-mono text-sm"
            />
          </div>

          {/* Historical activity count note */}
          <p className="text-sm text-admin-slate-500">
            Including{' '}
            <span className="text-admin-slate-400 font-medium">
              {historicalActivityCount}
            </span>{' '}
            historical {historicalActivityCount === 1 ? 'activity' : 'activities'} for
            deduplication.
          </p>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          type="button"
          onClick={onGenerate}
          disabled={isSubmitting || !promptText.trim()}
          className="bg-admin-accent-blue hover:bg-blue-600 disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Generate →'
          )}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onBack}
          disabled={isSubmitting}
          className="bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400"
        >
          ← Back
        </Button>
      </div>
    </div>
  );
}
